import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiGet, apiPost, ApiError } from './api';

export type Role = 'ADMIN' | 'CLIENTE';

export interface AuthUser {
  id: string;
  email: string;
  role: Role;
  mustChangePassword?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const me = await apiGet<{ id: string; email: string; role: Role; mustChangePassword?: boolean }>(
        '/api/auth/me',
      );
      setUser(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        // network or other error: keep user null but log
        console.error('auth refresh failed', err);
        setUser(null);
      }
    }
  }

  async function login(email: string, password: string): Promise<AuthUser> {
    const res = await apiPost<{ user: AuthUser }>('/api/auth/login', { email, password });
    setUser(res.user);
    return res.user;
  }

  async function logout() {
    try {
      await apiPost('/api/auth/logout');
    } finally {
      setUser(null);
    }
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
